import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import * as api from "../lib/supabase";
import { VoiceButton, VoiceResultPreview } from "../components/voice";
import { useApp } from "../lib/AppContext";
import { OfflineBanner } from "../components/OfflineBanner";
import { EventTypes } from "../lib/offlineQueue";

export default function Status({ job, costCodes, currentUser, orgId, onBack }) {
  const { offlineQueue } = useApp();
  const {
    isOnline,
    isSyncing,
    pendingCount,
    pendingByType,
    queueEvent,
    syncAll,
  } = offlineQueue;

  const [selectedCostCode, setSelectedCostCode] = useState(
    costCodes[0] || null,
  );
  const [quantity, setQuantity] = useState("");
  const [note, setNote] = useState("");
  const [updates, setUpdates] = useState([]);
  const [productionTotals, setProductionTotals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // Voice result state (new worker-based system)
  const [voiceResult, setVoiceResult] = useState(null);
  const [rawTranscript, setRawTranscript] = useState(""); // Store original voice transcript
  const [showTranscript, setShowTranscript] = useState(false); // Toggle to show raw transcript

  // Pending counts from offline queue
  const queuedStatusCount = pendingByType[EventTypes.STATUS_ADD] || 0;
  const queuedProductionCount = pendingByType[EventTypes.PRODUCTION_ADD] || 0;

  // Load today's updates and production totals
  useEffect(() => {
    if (job?.id) {
      loadUpdates();
      loadProductionTotals();
    }
  }, [job?.id]); // loadUpdates and loadProductionTotals are stable - defined in component scope

  // Update selected cost code when costCodes changes
  useEffect(() => {
    if (costCodes.length > 0 && !selectedCostCode) {
      setSelectedCostCode(costCodes[0]);
    }
  }, [costCodes, selectedCostCode]);

  const loadUpdates = async () => {
    setLoading(true);
    try {
      const today = new Date().toLocaleDateString("en-CA");
      const { data, error } = await api.supabase
        .from("t4s_status_updates")
        .select(
          `
          id,
          message,
          update_type,
          photo_url,
          created_at,
          posted_by:t4s_employees!posted_by(display_name)
        `,
        )
        .eq("job_id", job.id)
        .gte("created_at", today)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setUpdates(data || []);
    } catch (error) {
      console.error("Error loading updates:", error);
      setUpdates([]);
    } finally {
      setLoading(false);
    }
  };

  const loadProductionTotals = async () => {
    try {
      const totals = await api.getProductionTotals(job.id);
      setProductionTotals(totals);
    } catch (error) {
      console.error("Error loading production:", error);
      setProductionTotals([]);
    }
  };

  const addProduction = async (setTotal = false) => {
    if (!quantity || !selectedCostCode) return;
    setSending(true);

    const productionData = {
      costCodeId: selectedCostCode.id,
      quantity: parseFloat(quantity),
      notes: setTotal ? "Set as total" : "Added to total",
    };

    const statusMessage = `${setTotal ? "Set" : "Added"} ${quantity} ${selectedCostCode.name}`;

    try {
      // If offline, queue for later
      if (!isOnline) {
        await queueEvent(EventTypes.PRODUCTION_ADD, productionData, {
          orgId,
          jobId: job.id,
          userId: currentUser?.id,
        });

        await queueEvent(
          EventTypes.STATUS_ADD,
          { message: statusMessage, updateType: "note" },
          { orgId, jobId: job.id, userId: currentUser?.id },
        );

        setQuantity("");
        toast.info("Saved locally. Will sync when back online.");
        return;
      }

      // Online - save directly
      await api.addProduction({
        orgId,
        jobId: job.id,
        costCodeId: selectedCostCode.id,
        quantity: parseFloat(quantity),
        enteredBy: currentUser?.id,
        notes: setTotal ? "Set as total" : "Added to total",
      });

      // Also post a status update about the production
      await api.postStatusUpdate({
        orgId,
        jobId: job.id,
        postedBy: currentUser?.id,
        message: statusMessage,
        updateType: "note",
      });

      // Reload data
      await Promise.all([loadUpdates(), loadProductionTotals()]);
      setQuantity("");
    } catch (error) {
      console.error("Error adding production:", error);

      // On error, try to queue offline
      try {
        await queueEvent(EventTypes.PRODUCTION_ADD, productionData, {
          orgId,
          jobId: job.id,
          userId: currentUser?.id,
        });
        await queueEvent(
          EventTypes.STATUS_ADD,
          { message: statusMessage, updateType: "note" },
          { orgId, jobId: job.id, userId: currentUser?.id },
        );
        setQuantity("");
        toast.info("Network error. Saved locally for later sync.");
      } catch (queueError) {
        console.error("Failed to queue:", queueError);
        toast.error("Failed to save. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const sendNote = async () => {
    if (!note.trim()) return;
    setSending(true);

    const noteText = note.trim();

    try {
      // If offline, queue for later
      if (!isOnline) {
        await queueEvent(
          EventTypes.STATUS_ADD,
          { message: noteText, updateType: "note" },
          { orgId, jobId: job.id, userId: currentUser?.id },
        );

        setNote("");
        setRawTranscript("");
        setShowTranscript(false);
        toast.info("Note saved locally. Will sync when back online.");
        return;
      }

      // Online - save directly
      await api.postStatusUpdate({
        orgId,
        jobId: job.id,
        postedBy: currentUser?.id,
        message: noteText,
        updateType: "note",
      });

      await loadUpdates();
      setNote("");
      setRawTranscript("");
      setShowTranscript(false);
    } catch (error) {
      console.error("Error sending note:", error);

      // On error, try to queue offline
      try {
        await queueEvent(
          EventTypes.STATUS_ADD,
          { message: noteText, updateType: "note" },
          { orgId, jobId: job.id, userId: currentUser?.id },
        );
        setNote("");
        setRawTranscript("");
        setShowTranscript(false);
        toast.info("Network error. Note saved locally for later sync.");
      } catch (queueError) {
        console.error("Failed to queue:", queueError);
        toast.error("Failed to save note. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const getProductionForCode = (codeId) => {
    const prod = productionTotals.find((p) => p.costCodeId === codeId);
    return prod ? prod.todayQty : 0;
  };

  // Voice result handlers
  const handleVoiceResult = (result) => {
    setVoiceResult(result);
  };

  const handleVoiceError = (error) => {
    console.error("Voice error:", error);
    toast.error("Voice recording failed. Please try again or type your note.");
  };

  const handleVoiceConfirm = async () => {
    if (!voiceResult) return;

    const { extracted, transcript } = voiceResult;

    // Build structured note from extracted data
    let parts = [];

    if (extracted.activity) {
      parts.push(`Activity: ${extracted.activity}`);
    }
    if (extracted.area) {
      parts.push(`Area: ${extracted.area}`);
    }
    if (extracted.quantities && extracted.quantities.length > 0) {
      const qtyText = extracted.quantities
        .map((q) => `${q.qty} ${q.item}`)
        .join(", ");
      parts.push(`Quantities: ${qtyText}`);
    }
    if (extracted.materials && extracted.materials.length > 0) {
      parts.push(`Materials: ${extracted.materials.join(", ")}`);
    }
    if (extracted.notes) {
      parts.push(`Notes: ${extracted.notes}`);
    }
    if (extracted.issues && extracted.issues.length > 0) {
      parts.push(`[ISSUE] ${extracted.issues.join(", ")}`);
    }

    // Build final note text
    let noteText = parts.join(" | ");

    // Fallback to transcript if no structured data
    if (!noteText && transcript) {
      noteText = transcript;
    }

    // Store the raw transcript for "show original" feature
    setRawTranscript(transcript || "");

    // Set the note
    setNote((prev) => (prev ? `${prev}\n${noteText}` : noteText));

    // If quantities were extracted, try to add production
    if (
      extracted.quantities &&
      extracted.quantities.length > 0 &&
      selectedCostCode
    ) {
      const firstQty = extracted.quantities[0];
      const numericQty = parseFloat(firstQty.qty);
      if (!isNaN(numericQty)) {
        setQuantity(numericQty.toString());
      }
    }

    // Clear the voice result
    setVoiceResult(null);
  };

  const handleVoiceCancel = () => {
    setVoiceResult(null);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      {/* Offline Banner */}
      <OfflineBanner
        isOnline={isOnline}
        isSyncing={isSyncing}
        pendingCount={pendingCount}
        onSyncNow={syncAll}
      />

      {/* Header */}
      <div
        style={{
          background: "#1a5a8e",
          color: "white",
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "sticky",
          top: 0,
          zIndex: 40,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={onBack}
            style={{
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginLeft: "-8px",
              borderRadius: "8px",
              background: "transparent",
              border: "none",
              color: "white",
              cursor: "pointer",
            }}
          >
            <svg
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h1 style={{ fontSize: "18px", fontWeight: "600", margin: 0 }}>
            Status Update
          </h1>
        </div>
      </div>

      {/* Job Bar */}
      <div
        style={{
          background: "rgba(26,90,142,0.05)",
          padding: "12px 16px",
          borderBottom: "1px solid rgba(26,90,142,0.1)",
        }}
      >
        <div style={{ fontSize: "14px", color: "#1a5a8e", fontWeight: "500" }}>
          {job.job_number || job.id} - {job.name}
        </div>
      </div>

      {/* Quick Production Entry */}
      <div style={{ padding: "16px" }}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "600",
            color: "#64748b",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          Quick Production
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            padding: "16px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div>
              <label
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Cost Code
              </label>
              <select
                value={selectedCostCode?.code || ""}
                onChange={(e) =>
                  setSelectedCostCode(
                    costCodes.find((c) => c.code === e.target.value),
                  )
                }
                style={{
                  width: "100%",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px",
                  fontSize: "15px",
                  outline: "none",
                }}
              >
                {costCodes.map((cc) => (
                  <option key={cc.code} value={cc.code}>
                    {cc.code} - {cc.name}
                    {getProductionForCode(cc.id) > 0
                      ? ` (${getProductionForCode(cc.id)} today)`
                      : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: "13px",
                  color: "#64748b",
                  marginBottom: "4px",
                  display: "block",
                }}
              >
                Quantity
              </label>
              <input
                type="number"
                placeholder="0"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                style={{
                  width: "100%",
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: "10px",
                  padding: "12px",
                  fontSize: "15px",
                  outline: "none",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={() => addProduction(false)}
              disabled={sending || !quantity || !selectedCostCode}
              style={{
                flex: 1,
                background: sending ? "#94a3b8" : "#f1f5f9",
                color: "#475569",
                borderRadius: "10px",
                padding: "12px",
                fontWeight: "500",
                fontSize: "14px",
                border: "none",
                cursor: sending ? "wait" : "pointer",
                opacity: !quantity || !selectedCostCode ? 0.5 : 1,
              }}
            >
              + Add to Total
            </button>
            <button
              onClick={() => addProduction(true)}
              disabled={sending || !quantity || !selectedCostCode}
              style={{
                flex: 1,
                background: sending ? "#94a3b8" : "#1a5a8e",
                color: "white",
                borderRadius: "10px",
                padding: "12px",
                fontWeight: "500",
                fontSize: "14px",
                border: "none",
                cursor: sending ? "wait" : "pointer",
                opacity: !quantity || !selectedCostCode ? 0.5 : 1,
              }}
            >
              = Set Total
            </button>
          </div>
        </div>
      </div>

      {/* Status Note */}
      <div style={{ padding: "0 16px" }}>
        <div
          style={{
            fontSize: "13px",
            fontWeight: "600",
            color: "#64748b",
            textTransform: "uppercase",
            marginBottom: "8px",
          }}
        >
          Status Note
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            padding: "16px",
          }}
        >
          <textarea
            placeholder="How's it going? (e.g., '300 brick by coffee')"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{
              width: "100%",
              background: "transparent",
              border: "none",
              fontSize: "15px",
              resize: "none",
              outline: "none",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
          {/* Show raw transcript toggle - only visible when we have a transcript */}
          {rawTranscript && (
            <div style={{ marginTop: "8px", marginBottom: "8px" }}>
              <button
                onClick={() => setShowTranscript(!showTranscript)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#6b7280",
                  fontSize: "12px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 0",
                }}
              >
                <span>{showTranscript ? "▼" : "▶"}</span>
                <span>
                  {showTranscript ? "Hide" : "Show"} original transcript
                </span>
              </button>
              {showTranscript && (
                <div
                  style={{
                    marginTop: "6px",
                    padding: "10px 12px",
                    background: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    fontSize: "13px",
                    color: "#64748b",
                    fontStyle: "italic",
                    lineHeight: "1.5",
                  }}
                >
                  "{rawTranscript}"
                </div>
              )}
            </div>
          )}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: "12px",
              paddingTop: "12px",
              borderTop: "1px solid #f1f5f9",
            }}
          >
            <VoiceButton
              contextType="shift_note"
              jobId={job?.rawId || job?.id}
              screenContext={{
                costCode: selectedCostCode?.code,
                costCodeName: selectedCostCode?.name,
              }}
              onResult={handleVoiceResult}
              onError={handleVoiceError}
            />
            <button
              onClick={sendNote}
              disabled={sending || !note.trim()}
              style={{
                background: sending ? "#94a3b8" : "#1a5a8e",
                color: "white",
                padding: "10px 20px",
                borderRadius: "10px",
                fontWeight: "500",
                fontSize: "14px",
                border: "none",
                cursor: sending ? "wait" : "pointer",
                opacity: !note.trim() ? 0.5 : 1,
              }}
            >
              {sending ? "Sending..." : "Send Update"}
            </button>
          </div>
        </div>
      </div>

      {/* Today's Updates */}
      <div style={{ padding: "16px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <div
            style={{
              fontSize: "13px",
              fontWeight: "600",
              color: "#64748b",
              textTransform: "uppercase",
            }}
          >
            Today's Updates
          </div>
          {loading && (
            <span style={{ fontSize: "12px", color: "#94a3b8" }}>
              Loading...
            </span>
          )}
        </div>
        <div
          style={{
            background: "white",
            borderRadius: "16px",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          {updates.length === 0 && !loading ? (
            <div
              style={{ padding: "24px", textAlign: "center", color: "#94a3b8" }}
            >
              No updates yet today
            </div>
          ) : (
            updates.map((update, i) => (
              <div
                key={update.id}
                style={{
                  padding: "12px 16px",
                  borderBottom:
                    i < updates.length - 1 ? "1px solid #f1f5f9" : "none",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: "4px",
                  }}
                >
                  <span style={{ fontSize: "13px", color: "#94a3b8" }}>
                    {formatTime(update.created_at)}
                    {update.posted_by?.display_name && (
                      <span style={{ marginLeft: "8px" }}>
                        • {update.posted_by.display_name}
                      </span>
                    )}
                  </span>
                  {update.update_type !== "note" && (
                    <span
                      style={{
                        padding: "2px 8px",
                        borderRadius: "6px",
                        background:
                          update.update_type === "issue"
                            ? "#fee2e2"
                            : update.update_type === "delay"
                              ? "#fef3c7"
                              : update.update_type === "weather"
                                ? "#e0e7ff"
                                : "#dcfce7",
                        color:
                          update.update_type === "issue"
                            ? "#dc2626"
                            : update.update_type === "delay"
                              ? "#92400e"
                              : update.update_type === "weather"
                                ? "#3730a3"
                                : "#166534",
                        fontSize: "11px",
                        fontWeight: "600",
                        textTransform: "capitalize",
                      }}
                    >
                      {update.update_type}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: "15px", color: "#475569" }}>
                  <FormattedUpdateMessage message={update.message} />
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Voice Result Preview Modal */}
      {voiceResult && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "16px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "rgba(0,0,0,0.5)",
            }}
            onClick={handleVoiceCancel}
          />
          <div
            style={{ position: "relative", width: "100%", maxWidth: "400px" }}
          >
            <VoiceResultPreview
              result={voiceResult}
              onConfirm={handleVoiceConfirm}
              onCancel={handleVoiceCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Format update messages with structured data and highlighted issues
function FormattedUpdateMessage({ message }) {
  if (!message) return null;

  // Check if it's a structured message (contains " | " separators or [ISSUE])
  const hasStructure = message.includes(" | ") || message.includes("[ISSUE]");

  if (!hasStructure) {
    return <span>{message}</span>;
  }

  // Split by " | " and render each part
  const parts = message.split(" | ");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
      {parts.map((part, i) => {
        const isIssue = part.startsWith("[ISSUE]");
        const displayText = isIssue ? part.replace("[ISSUE] ", "") : part;

        if (isIssue) {
          return (
            <span
              key={i}
              style={{
                color: "#ea580c",
                fontWeight: "600",
                background: "#fff7ed",
                padding: "4px 8px",
                borderRadius: "6px",
                border: "1px solid #fed7aa",
              }}
            >
              ⚠️ {displayText}
            </span>
          );
        }

        // Parse label:value format
        const colonIndex = part.indexOf(":");
        if (colonIndex > 0) {
          const label = part.substring(0, colonIndex);
          const value = part.substring(colonIndex + 1).trim();
          return (
            <span key={i}>
              <span style={{ fontWeight: "600", color: "#334155" }}>
                {label}:
              </span>{" "}
              <span>{value}</span>
            </span>
          );
        }

        return <span key={i}>{part}</span>;
      })}
    </div>
  );
}
